import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/auth";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";
import {
  changeMyAdminEmail,
  changeMyAdminPassword,
  getMyAccount,
  updateMyAdminProfile,
  type AdminAccount,
} from "@/lib/api";
import { useRoleLabel } from "@/lib/roles";

/**
 * Platform super-admin "user home" (Phase 47). Reached from the email chip
 * in the admin shell. Shows the admin's own identity (role, last login,
 * created) and lets them change their display name, login email, and
 * password — the self-service surface the admin SPA was missing (the
 * Super Admins page manages *peers*, never yourself).
 *
 * Email + password changes confirm the current password server-side and are
 * audit-logged. A successful email or name change calls {@code refresh()} so
 * the shell chrome reflects it without a reload.
 */
export default function AccountPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const [account, setAccount] = useState<AdminAccount | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((a) => !cancelled && setAccount(a))
      .catch((e) => !cancelled && setLoadErr(toErrorMessage(e, t("account.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  const email = account?.email ?? user?.email ?? "";
  const fullName = account?.fullName ?? user?.full_name ?? null;
  const initials = initialsFor(fullName, email);
  const reload = () => setReloadAt((n) => n + 1);

  return (
    <PageContainer title={t("account.title")} subtitle={t("account.subtitle")}>
      <ErrorBanner message={loadErr} />
      <div className="grid lg:grid-cols-[20rem_1fr] gap-3">
        <Card>
          <div className="flex flex-col items-center text-center gap-2 py-2">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-accent/15 text-lg font-semibold text-accent">
              {initials}
            </span>
            <p className="text-base font-semibold">{fullName ?? email}</p>
            <p className="text-xs text-muted-foreground">{email}</p>
            <Badge tone="warning">{roleLabel(account?.role ?? user?.role ?? "super_admin")}</Badge>
          </div>
        </Card>

        <div className="space-y-3">
          <ProfileCard account={account} onChanged={reload} />
          <EmailCard account={account} onChanged={reload} />
          <PasswordCard />

          <Card title={t("account.identityCard")}>
            <dl className="text-sm space-y-1.5">
              <Row label={t("account.fieldRole")}>{account?.role ?? user?.role}</Row>
              <Row label={t("account.fieldId")}>
                <span className="font-mono text-xs">{account?.id ?? user?.id}</span>
              </Row>
              <Row label={t("account.fieldLastLogin")}>
                {account?.lastLoginAt ?? t("account.never")}
              </Row>
              <Row label={t("account.fieldCreated")}>{account?.createdAt ?? "—"}</Row>
            </dl>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}

function initialsFor(fullName: string | null, email: string): string {
  const fn = fullName?.trim();
  if (fn && fn.length > 0) {
    const parts = fn.split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (a + b).toUpperCase() || email.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

/** Maps the backend's opaque {error} codes onto localised copy. */
function useAccountError() {
  const { t } = useTranslation();
  return (e: unknown): string => {
    const code = toErrorMessage(e);
    const known: Record<string, string> = {
      invalid_password: t("account.errWrongPassword"),
      email_taken: t("account.errEmailTaken"),
    };
    return known[code] ?? code;
  };
}

function SuccessLine({ message }: { message: string | null }) {
  if (!message) return null;
  return <p className="mt-2 text-xs text-emerald-400">{message}</p>;
}

/* ── Profile (display name) ──────────────────────────────────────────── */

function ProfileCard({
  account,
  onChanged,
}: {
  account: AdminAccount | null;
  onChanged: () => void;
}) {
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const toError = useAccountError();
  const [name, setName] = useState("");
  const [seeded, setSeeded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Seed the input once the account loads (without clobbering edits in flight).
  useEffect(() => {
    if (account && !seeded) {
      setName(account.fullName ?? "");
      setSeeded(true);
    }
  }, [account, seeded]);

  const dirty = name.trim() !== (account?.fullName ?? "").trim();

  async function save() {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await updateMyAdminProfile(name.trim());
      await refresh();
      onChanged();
      setOk(t("account.profileChanged"));
    } catch (e) {
      setErr(toError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("account.profileCard")} description={t("account.profileDesc")}>
      <ErrorBanner message={err} />
      <Field label={t("account.nameLabel")}>
        <TextInput
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setOk(null);
          }}
          placeholder={t("account.namePlaceholder")}
          autoComplete="name"
        />
      </Field>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !dirty}>
          {busy ? t("account.saving") : t("account.save")}
        </Button>
        <SuccessLine message={ok} />
      </div>
    </Card>
  );
}

/* ── Email address ───────────────────────────────────────────────────── */

function EmailCard({
  account,
  onChanged,
}: {
  account: AdminAccount | null;
  onChanged: () => void;
}) {
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const toError = useAccountError();
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const current = account?.email ?? "";
  const canSubmit =
    newEmail.trim().length > 0 &&
    newEmail.trim().toLowerCase() !== current.toLowerCase() &&
    currentPassword.length > 0;

  async function save() {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await changeMyAdminEmail(currentPassword, newEmail.trim());
      await refresh();
      onChanged();
      setOk(t("account.emailChanged"));
      setNewEmail("");
      setCurrentPassword("");
    } catch (e) {
      setErr(toError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("account.emailCard")} description={t("account.emailDesc")}>
      <ErrorBanner message={err} />
      <div className="space-y-3">
        <Field label={t("account.currentEmailLabel")}>
          <TextInput value={current} readOnly disabled />
        </Field>
        <Field label={t("account.newEmailLabel")}>
          <TextInput
            type="email"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setOk(null);
            }}
            autoComplete="email"
            placeholder="admin@platform.example"
          />
        </Field>
        <Field label={t("account.currentPasswordLabel")}>
          <TextInput
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !canSubmit}>
          {busy ? t("account.saving") : t("account.save")}
        </Button>
        <SuccessLine message={ok} />
      </div>
    </Card>
  );
}

/* ── Password ────────────────────────────────────────────────────────── */

const MIN_PASSWORD = 12;

function PasswordCard() {
  const { t } = useTranslation();
  const toError = useAccountError();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const canSubmit =
    currentPassword.length > 0 && newPassword.length > 0 && confirm.length > 0;

  async function save() {
    setErr(null);
    setOk(null);
    if (newPassword.length < MIN_PASSWORD) {
      setErr(t("account.errPasswordTooShort"));
      return;
    }
    if (newPassword !== confirm) {
      setErr(t("account.errPasswordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await changeMyAdminPassword(currentPassword, newPassword);
      setOk(t("account.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
    } catch (e) {
      setErr(toError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("account.passwordCard")} description={t("account.passwordDesc")}>
      <ErrorBanner message={err} />
      <div className="space-y-3">
        <Field label={t("account.currentPasswordLabel")}>
          <TextInput
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </Field>
        <Field label={t("account.newPasswordLabel")} hint={t("account.passwordRule")}>
          <TextInput
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setOk(null);
            }}
            autoComplete="new-password"
          />
        </Field>
        <Field label={t("account.confirmPasswordLabel")}>
          <TextInput
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !canSubmit}>
          {busy ? t("account.saving") : t("account.save")}
        </Button>
        <SuccessLine message={ok} />
      </div>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
